/**
 * The repo-picker's search predicate, shared by the server route that answers
 * `GET /github/repos` and the web hook that filters the cached list in the
 * browser.
 *
 * It lives here because both sides must agree exactly: the route hands the web
 * client the caller's whole accessible repo set (flagged `complete`), and the
 * client then filters it locally for every keystroke instead of issuing a
 * request per prefix. If the two predicates drifted, the same query would
 * produce different rows depending on which path served it.
 *
 * @module @reflex/plugin-github/shared/repo-search
 */

/** The fields {@link matchesRepoQuery} looks at — satisfied by both the
 * server's `RepoResult` and the web's `GitHubRepo`. */
export interface RepoSearchable {
  fullName: string;
  name: string;
}

/** Normalizes a raw search box value into the form {@link matchesRepoQuery}
 * compares against. Exported so callers key caches/queries on the same
 * normalized string the filter uses. */
export function normalizeRepoQuery(raw: string | undefined): string {
  return raw?.trim().toLowerCase() ?? '';
}

/** Substring match on `owner/name` or the bare repo name. `query` must already
 * be normalized by {@link normalizeRepoQuery}. */
export function matchesRepoQuery(repo: RepoSearchable, query: string): boolean {
  if (!query) return true;
  return repo.fullName.toLowerCase().includes(query) || repo.name.toLowerCase().includes(query);
}

/** {@link matchesRepoQuery} over a list, preserving input order. */
export function filterRepos<T extends RepoSearchable>(repos: readonly T[], query: string): T[] {
  if (!query) return [...repos];
  return repos.filter((repo) => matchesRepoQuery(repo, query));
}
